import axios from 'axios';

const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const adminToken = localStorage.getItem('luxortek_admin_token');
  const buyerToken = localStorage.getItem('luxortek_buyer_token');

  if (config.url?.includes('/admin/') || config.url?.includes('/admin?')) {
    if (adminToken) config.headers.Authorization = `Bearer ${adminToken}`;
  } else {
    const token = buyerToken || adminToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      if (url.includes('/buyer/')) {
        localStorage.removeItem('luxortek_buyer_token');
        localStorage.removeItem('luxortek_buyer_info');
        if (window.location.pathname.startsWith('/buyer') && !window.location.pathname.includes('/login')) {
          window.location.href = '/buyer/login';
        }
      } else if (url.includes('/admin/')) {
        localStorage.removeItem('luxortek_admin_token');
        localStorage.removeItem('luxortek_admin_info');
        if (window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/login')) {
          window.location.href = '/admin/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Public endpoints
export const getProducts = (params) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const getCategories = () => api.get('/categories');
export const getBrands = () => api.get('/brands');
export const getProductFacets = (params) => api.get('/products/facets', { params });
export const getProductsLastUpdated = () => api.get('/products/last-updated');
export const getPublicSettings = () => api.get('/settings/public');
export const submitContact = (data) => api.post('/contact', data);
export const createOrder = (data) => api.post('/orders', data);
export const createQuote = (data) => api.post('/quotes', data);

// Admin auth
export const adminLogin = (data) => api.post('/admin/login', data);
export const getAdminMe = () => api.get('/admin/me');
export const adminChangePassword = (data) => api.post('/admin/change-password', data);

// Buyer change password
export const buyerChangePassword = (data) => api.post('/buyer/change-password', data);

// Admin products
export const getAdminProducts = (params) => api.get('/admin/products', { params });
export const getAdminProductCounts = (params) => api.get('/admin/products/counts', { params });
export const getReviewQueue = () => api.get('/admin/products/review-queue');
export const approveProduct = (id) => api.post(`/admin/products/${id}/approve`);
export const rejectProduct = (id) => api.post(`/admin/products/${id}/reject`);
export const approveAllProducts = () => api.post('/admin/products/approve-all');
export const bulkApproveProducts = (ids) => api.post('/admin/products/bulk-approve', { product_ids: ids });
export const bulkRejectProducts = (ids) => api.post('/admin/products/bulk-reject', { product_ids: ids });
export const countFilteredProducts = (filters) => api.post('/admin/products/count-filtered', { filters });
export const approveAllFiltered = (filters) => api.post('/admin/products/approve-all-filtered', { filters });
export const rejectAllFiltered = (filters) => api.post('/admin/products/reject-all-filtered', { filters });
export const deleteAllProducts = (data = {}) => api.post('/admin/products/delete-all', data);
export const deleteProduct = (id) => api.delete(`/admin/products/${id}`);
export const repriceAllProducts = (data = {}) => api.post('/admin/products/reprice', data);
export const getAdminProduct = (id) => api.get(`/admin/products/${id}`);
export const updateAdminProduct = (id, data) => api.patch(`/admin/products/${id}`, data);

// Product Images
export const uploadProductImage = (productId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/admin/products/${productId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteProductImage = (productId) => api.delete(`/admin/products/${productId}/image`);
export const getProductImageUrl = (productId) => `${API_URL}/products/${productId}/image`;

export const importInventory = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Flexible import endpoints
export const analyzeImportFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/import/analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const previewImport = (config) => api.post('/admin/import/preview', config);
export const executeImport = (config) => api.post('/admin/import/execute', config);

// Mapping presets
export const getMappingPresets = () => api.get('/admin/import/presets');
export const saveMappingPreset = (data) => api.post('/admin/import/presets', data);
export const deleteMappingPreset = (id) => api.delete(`/admin/import/presets/${id}`);

// Import batch pending count & bulk approve
export const getLatestPendingCount = () => api.get('/admin/import/latest/pending-count');
export const approveAllLatestBatch = () => api.post('/admin/import/latest/approve-all');

// Import history
export const getImportHistory = () => api.get('/admin/import/history');
export const deleteImportHistory = (id) => api.delete(`/admin/import/history/${id}`);
export const getImportFileDownloadUrl = (id) => `${API_URL}/admin/import/history/${id}/download`;
export const getImportErrorReportUrl = (id) => `${API_URL}/admin/import/error-report/${id}`;

// Admin orders
export const getAdminOrders = (params) => api.get('/admin/orders', { params });
export const getAdminOrder = (id) => api.get(`/admin/orders/${id}`);
export const updateOrderStatus = (id, data) => api.patch(`/admin/orders/${id}/status`, null, { params: data });
export const updateOrderNotes = (id, notes) => api.patch(`/admin/orders/${id}/notes`, null, { params: { supplier_confirmation_notes: notes } });
export const sendProforma = (id) => api.post(`/admin/orders/${id}/send-proforma`);
export const getExportProviderUrl = (id) => `${API_URL}/admin/orders/${id}/export-provider.xlsx`;

// Admin settings
export const getAdminSettings = () => api.get('/admin/settings');
export const updateSettings = (data) => api.patch('/admin/settings', data);

// Admin stats
export const getAdminStats = () => api.get('/admin/stats');

// Admin contacts
export const getAdminContacts = () => api.get('/admin/contacts');

// Wholesale Leads
export const registerWholesaleLead = (data) => api.post('/wholesale/register', data);
export const getLeads = (params) => api.get('/admin/leads', { params });
export const getLeadDetail = (id) => api.get(`/admin/leads/${id}`);
export const updateLead = (id, data) => api.put(`/admin/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/admin/leads/${id}`);
export const approveLeadWelcome = (id) => api.post(`/admin/leads/${id}/approve-welcome`);
export const getLeadsExportUrl = () => `${API_URL}/admin/leads-export`;

// Buyer Auth
export const buyerLoginApi = (data) => api.post('/buyer/login', data);
export const setPasswordApi = (data) => api.post('/buyer/set-password', data);
export const getBuyerMe = () => api.get('/buyer/me');
export const forgotPasswordApi = (data) => api.post('/auth/forgot-password', data);
export const resetPasswordApi = (data) => api.post('/auth/reset-password', data);

// Buyer Cart
export const getBuyerCart = () => api.get('/buyer/cart');
export const addToCartApi = (productId) => api.post('/buyer/cart/add', { product_id: productId });
export const removeFromCartApi = (productId) => api.delete(`/buyer/cart/${productId}`);
export const clearCartApi = () => api.delete('/buyer/cart');
export const submitCartApi = () => api.post('/buyer/cart/submit');
export const importCsvToCart = (formData) => api.post('/buyer/cart/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getCartImportTemplateUrl = () => `${API_URL}/buyer/cart/import-template`;
export const getProductsExportUrl = (params) => {
  const qs = new URLSearchParams();
  if (params.category) qs.set('category', params.category);
  if (params.grade) qs.set('grade', params.grade);
  if (params.oem) qs.set('oem', params.oem);
  if (params.search) qs.set('search', params.search);
  return `${API_URL}/products/export.xlsx?${qs.toString()}`;
};

// Buyer Orders
export const getBuyerQuotes = (limit) => api.get('/buyer/quotes', { params: { limit } });
export const getBuyerOrders = (limit) => api.get('/buyer/orders', { params: { limit } });
export const getBuyerOrder = (id) => api.get(`/buyer/orders/${id}`);
export const reorderApi = (id) => api.post(`/buyer/orders/${id}/reorder`);

// Buyer Quick Reorder
export const getQuickReorderData = () => api.get('/buyer/quick-reorder');
export const quickReorderApi = (data) => api.post('/buyer/quick-reorder', data);

// Buyer Saved Lists
export const getSavedLists = () => api.get('/buyer/saved-lists');
export const createSavedList = (data) => api.post('/buyer/saved-lists', data);
export const getSavedList = (id) => api.get(`/buyer/saved-lists/${id}`);
export const updateSavedList = (id, data) => api.put(`/buyer/saved-lists/${id}`, data);
export const deleteSavedList = (id) => api.delete(`/buyer/saved-lists/${id}`);
export const applySavedList = (id, data) => api.post(`/buyer/saved-lists/${id}/apply`, data);

// Admin Buyer Management
export const convertLeadToBuyer = (leadId) => api.post(`/admin/leads/${leadId}/convert-buyer`);
export const getAdminBuyers = (params) => api.get('/admin/buyers', { params });
export const getAdminBuyer = (id) => api.get(`/admin/buyers/${id}`);
export const updateBuyerStatus = (id, data) => api.patch(`/admin/buyers/${id}/status`, data);
export const editBuyer = (id, data) => api.patch(`/admin/buyers/${id}`, data);
export const deleteBuyer = (id) => api.delete(`/admin/buyers/${id}`);
export const mergeBuyers = (data) => api.post('/admin/buyers/merge', data);
export const findBuyerDuplicates = (id) => api.get(`/admin/buyers/${id}/find-duplicates`);

// Admin Lead Management
export const editLead = (id, data) => api.patch(`/admin/leads/${id}/edit`, data);
export const mergeLeads = (data) => api.post('/admin/leads/merge', data);
export const findLeadDuplicates = (id) => api.get(`/admin/leads/${id}/find-duplicates`);

// Admin Contact Management
export const editContact = (id, data) => api.patch(`/admin/contacts/${id}`, data);
export const deleteContact = (id) => api.delete(`/admin/contacts/${id}`);
export const mergeContacts = (data) => api.post('/admin/contacts/merge', data);
export const findContactDuplicates = (id) => api.get(`/admin/contacts/${id}/find-duplicates`);

// Unified Admin Search
export const adminSearch = (q) => api.get('/admin/search', { params: { q } });

// Admin Quotes
export const getAdminQuotes = (params) => api.get('/admin/quotes', { params });
export const getAdminQuote = (quoteId) => api.get(`/admin/quotes/${quoteId}`);
export const updateQuoteStatus = (quoteId, data) => api.patch(`/admin/quotes/${quoteId}/status`, data);
export const editQuote = (quoteId, data) => api.patch(`/admin/quotes/${quoteId}`, data);
export const deleteQuote = (quoteId) => api.delete(`/admin/quotes/${quoteId}`);
export const cancelQuote = (quoteId) => api.post(`/admin/quotes/${quoteId}/cancel`);
export const reopenQuote = (quoteId, data) => api.post(`/admin/quotes/${quoteId}/reopen`, data);
export const convertQuoteToOrder = (quoteId) => api.post(`/admin/quotes/${quoteId}/convert-to-order`);
export const editOrder = (orderId, data) => api.patch(`/admin/orders/${orderId}`, data);
export const cancelOrder = (orderId) => api.post(`/admin/orders/${orderId}/cancel`);
export const deleteOrder = (orderId) => api.delete(`/admin/orders/${orderId}`);

// PDF Proforma
export const getProformaPdfUrl = (orderId) => `${API_URL}/admin/orders/${orderId}/proforma-pdf`;

// Resend Status Email
export const resendStatusEmail = (orderId) => api.post(`/admin/orders/${orderId}/resend-status-email`);

// SharePoint Sync
export const testSharePointConnection = () => api.post('/admin/sharepoint/test-connection');
export const triggerSharePointSync = () => api.post('/admin/sharepoint/sync-now');
export const getSharePointSyncLog = () => api.get('/admin/sharepoint/sync-log');

// Customer Price List Export
export const exportCustomerPriceList = (params) =>
  api.get('/admin/export/customer-price-list', { params, responseType: 'blob' });

// Admin Alert Settings (cache-busted to avoid stale proxy/CDN responses)
export const getAlertSettings = () => api.get('/admin/alerts/settings', {
  params: { _t: Date.now() },
  headers: { 'Cache-Control': 'no-cache, no-store' },
});
export const updateAlertSettings = (data) => api.put('/admin/alerts/settings', data);
export const sendTestAlert = () => api.post('/admin/alerts/test');
export const getAlertLog = (limit = 50) => api.get('/admin/alerts/log', { params: { limit } });

export default api;

// ── Recovered Admin Module API Functions ──────────────────────────

const _formatFastApiItem = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item !== 'object') return String(item);
  const msg = item.msg || item.message || '';
  const loc = Array.isArray(item.loc) ? item.loc.filter((p) => p !== 'body').join('.') : '';
  if (loc && msg) return `${loc}: ${msg}`;
  return msg || JSON.stringify(item);
};

export const readApiError = (error, fallback = 'Request failed') => {
  if (error == null) return fallback;
  if (typeof error === 'string') return error;
  // Network / no-response axios error
  if (!error.response) {
    return error.message || fallback;
  }
  const data = error.response.data;
  if (data == null) return error.message || fallback;
  if (typeof data === 'string') return data;
  // Common FastAPI shapes
  const detail = data.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map(_formatFastApiItem).filter(Boolean);
    if (parts.length) return parts.join(' · ');
  } else if (detail && typeof detail === 'object') {
    const s = _formatFastApiItem(detail);
    if (s) return s;
  }
  if (typeof data.message === 'string') return data.message;
  if (typeof data.error === 'string') return data.error;
  return error.message || fallback;
};

// Attach our error normaliser to any axios instance. Safe to call multiple
// times (each instance has its own interceptor chain).
export const attachErrorNormalizer = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const safe = readApiError(error, '');
        if (safe) {
          // Mutate the existing shape so `err.response.data.detail` reads as
          // a string for any legacy call-site, and expose `.formattedMessage`
          // for new code that wants to be explicit.
          if (error.response && error.response.data && typeof error.response.data === 'object') {
            const d = error.response.data.detail;
            if (d != null && typeof d !== 'string') {
              // Preserve the original structured detail so call-sites
              // that need to branch on error codes (e.g. Zoho 503/409)
              // can still read the original shape.
              error.response.data.detail_raw = d;
              error.response.data.detail = safe;
            }
          }
          error.formattedMessage = safe;
        }
      } catch (_e) {
        // Never let the normaliser itself throw.
      }
      return Promise.reject(error);
    }
  );
};


// Public endpoints
export const getProductsGrouped = (params) => api.get('/products/grouped', { params });
export const buyerCreateQuoteRequest = (data) => api.post('/buyer/quote-request', data);

// Admin → Zoho Books bridge (Phase 1)
export const adminCreateZohoQuote = (quoteId, data = {}) =>
  api.post(`/admin/quotes/${quoteId}/zoho/create`, data);

// Admin → Master contacts export (XLSX, binary)
export const adminExportMasterContacts = () =>
  api.get('/admin/contacts/export-all.xlsx', { responseType: 'blob' });

// Admin → Supplier-aware duplicate-group repair (Feb-18 fix)
export const adminRepairSupplierDuplicates = (dryRun = false) =>
  api.post('/admin/products/repair-supplier-duplicates', null, {
    params: { dry_run: dryRun },
  });

// Admin auth
export const repairCnbNormalize = () => api.post('/admin/products/repair-cnb-normalize');
export const getInventoryDashboard = () => api.get('/admin/products/inventory-dashboard');
export const getGroupedSummary = (params = {}) => api.get('/admin/products/grouped-summary', { params });
export const getGroupUnits = (group_key, limit = 200) => api.post('/admin/products/group-units', { group_key, limit });
export const setGroupPricing = (group_key, mode, unit_price) => api.post('/admin/products/group-pricing', { group_key, mode, unit_price });
export const setGroupCostOverride = (group_key, lowest_cost) => api.post('/admin/products/group-cost-override', { group_key, lowest_cost });
export const clearGroupCostOverride = (group_key) => api.post('/admin/products/group-cost-override', { group_key, clear: true });
export const setGroupTierOverride = (group_key, tier) => api.post('/admin/products/group-tier-override', { group_key, tier });
export const clearGroupTierOverride = (group_key) => api.post('/admin/products/group-tier-override', { group_key, clear: true });
export const bulkPricing = (group_keys, action, value) => api.post('/admin/products/bulk-pricing', { group_keys, action, value });
export const recalculateAllAutoPriced = (data = {}) => api.post('/admin/products/recalculate-auto', data);

// Product Images
export const dryRunImport = (config) => api.post('/admin/import/dry-run', config, { timeout: 60000 });
export const commitImport = (body) => api.post('/admin/import/commit', body, { timeout: 30000 });
export const getImportJobStatus = (jobId) => api.get(`/admin/import/status/${jobId}`);

// Mapping presets
export const purgePendingReview = (params = {}) =>
  api.delete('/admin/products/pending-review/purge', { params });

// Import history
export const markContactsViewed = () => api.post('/admin/contacts/mark-viewed');

// Wholesale Leads
export const createLeadManual = (data, allowDuplicate = false) =>
  api.post(`/admin/leads?allow_duplicate=${allowDuplicate}`, data);
export const validateSetupToken = (token) => api.get('/auth/validate-setup-token', { params: { token } });
export const resendActivationApi = (data) => api.post('/auth/resend-activation', data);
export const resendBuyerWelcome = (buyerId) => api.post(`/admin/buyers/${buyerId}/resend-welcome`);

// Buyer Cart
export const getGroupedExportUrl = (params) => {
  const qs = new URLSearchParams();
  if (params.category) qs.set('category', params.category);
  if (params.grade) qs.set('grade', params.grade);
  if (params.oem) qs.set('oem', params.oem);
  if (params.search) qs.set('search', params.search);
  return `${API_URL}/products/grouped/export.xlsx?${qs.toString()}`;
};

// Buyer Orders
export const shareSavedList = (id) => api.post(`/buyer/saved-lists/${id}/share`);
export const unshareSavedList = (id) => api.delete(`/buyer/saved-lists/${id}/share`);
export const getSharedList = (token) => api.get(`/shared-lists/${token}`);

// Admin Buyer Management
export const convertLeadToProspect = (leadId, allowDuplicate = false) =>
  api.post(`/admin/leads/${leadId}/convert-prospect${allowDuplicate ? '?allow_duplicate=true' : ''}`);
export const convertProspectToBuyer = (prospectId, allowDuplicate = false, sendEmail = true) => {
  const params = new URLSearchParams();
  if (allowDuplicate) params.set('allow_duplicate', 'true');
  if (!sendEmail) params.set('send_email', 'false');
  const qs = params.toString() ? `?${params.toString()}` : '';
  return api.post(`/admin/prospects/${prospectId}/convert-to-buyer${qs}`);
};
export const setTempPassword = (id, data) => api.post(`/admin/buyers/${id}/set-temp-password`, data);
export const generateTempPassword = (id) => api.post(`/admin/buyers/${id}/generate-temp-password`);

// Customer 360 (Feb 2026)
export const getBuyerCustomer360 = (id) => api.get(`/admin/buyers/${id}/customer360`);
export const getBuyerCustomer360Timeline = (id, limit = 120) => api.get(`/admin/buyers/${id}/customer360/timeline`, { params: { limit } });
export const listBuyerNotes = (id) => api.get(`/admin/buyers/${id}/notes`);
export const createBuyerNote = (id, body) => api.post(`/admin/buyers/${id}/notes`, { body });
export const deleteBuyerNote = (id, noteId) => api.delete(`/admin/buyers/${id}/notes/${noteId}`);

// Buyer Force Change Password
export const forceChangePasswordApi = (data) => api.post('/buyer/force-change-password', data);

// Sourcing Requests (public)
export const submitSourcingRequest = (data) => api.post('/sourcing-requests', data);
export const subscribeToInventoryUpdates = (data) => api.post('/subscribers', data);
export const getAdminSubscribers = (params) => api.get('/admin/subscribers', { params });
export const deleteAdminSubscriber = (id) => api.delete(`/admin/subscribers/${id}`);
export const getAnalyticsOverview = () => api.get('/admin/analytics/overview');
export const getAnalyticsTopCountries = (limit = 10) => api.get('/admin/analytics/top-countries', { params: { limit } });
export const getAnalyticsMostRequestedProducts = (params) => api.get('/admin/analytics/most-requested-products', { params });
export const getAnalyticsTopBrands = (params) => api.get('/admin/analytics/top-brands', { params });
export const getAnalyticsTopModels = (params) => api.get('/admin/analytics/top-models', { params });
export const getAnalyticsBuyerSegments = (params) => api.get('/admin/analytics/top-buyer-segments', { params });

// Sourcing Requests (admin)
export const getAdminSourcingRequests = (params) => api.get('/admin/sourcing-requests', { params });
export const updateSourcingRequestStatus = (id, data) => api.patch(`/admin/sourcing-requests/${id}/status`, data);
// Feb-2026 — Internal Work card fields (assigned_to, priority, follow-up dates, notes, etc).
export const updateSourcingInternal = (id, data) => api.patch(`/admin/sourcing-requests/${id}/internal`, data);
export const getSourcingOptions = () => api.get('/admin/sourcing-requests/meta/options');
export const getSourcingDashboardStats = () => api.get('/admin/sourcing-requests/stats/dashboard');
export const migrateSourcingStatuses = (dryRun = false) =>
  api.post(`/admin/sourcing-requests/migrate-statuses${dryRun ? '?dry_run=true' : ''}`);
export const getSourcingWidgetStats = () => api.get('/admin/sourcing-requests/stats/widget');
export const getSourcingRequestsByIds = (ids) => api.post('/admin/sourcing-requests/by-ids', { ids });
export const createQuoteFromSourcing = (id) => api.post(`/admin/sourcing-requests/${id}/create-quote`);
export const deleteSourcingRequest = (id) => api.delete(`/admin/sourcing-requests/${id}`);

// Bulk Lots (public)
export const getPublicBulkLots = (params) => api.get('/bulk-lots', { params });
export const getBulkLotFilters = () => api.get('/bulk-lots/filters');
export const getPublicBulkLot = (slug) => api.get(`/bulk-lots/${slug}`);

// Bulk Lots (admin)
export const getAdminBulkLots = (params) => api.get('/admin/bulk-lots', { params });
export const getAdminBulkLot = (id) => api.get(`/admin/bulk-lots/${id}`);
export const createBulkLot = (data) => api.post('/admin/bulk-lots', data);
export const updateBulkLot = (id, data) => api.put(`/admin/bulk-lots/${id}`, data);
export const deleteBulkLot = (id) => api.delete(`/admin/bulk-lots/${id}`);
export const quickImportAnalyze = (formData) => api.post('/admin/bulk-lots/quick-import/analyze', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
export const quickImportExecute = (data) => api.post('/admin/bulk-lots/quick-import/execute', data, { timeout: 60000 });
export const deleteAllBulkLots = (data) => api.post('/admin/bulk-lots/delete-all', data);
export const backfillBulkLotGrades = () => api.post('/admin/bulk-lots/backfill-grades');
export const backfillBulkLotPricing = () => api.post('/admin/bulk-lots/backfill-pricing');
export const deleteProductsByCategory = (category) => api.delete(`/admin/products/category/${category}`);

// Gallery
export const getPublicGallery = () => api.get('/gallery');
export const downloadProductList = (slug) => api.get(`/bulk-lots/${slug}/product-list`, { responseType: 'blob' });
export const downloadLotExcel = (slug) => api.get(`/bulk-lots/${slug}/download`, { responseType: 'blob' });

// Admin Lead Management
export const convertContactToQuote = (id) => api.post(`/admin/contacts/${id}/convert-quote`);
export const extractQuoteFromMessage = (quoteId) => api.post(`/admin/quotes/${quoteId}/extract-from-message`);

// Unified Admin Search
export const exportBulkLotsCustomer = () =>
  api.get('/admin/export/bulk-lots-customer', { responseType: 'blob' });
export const exportBulkLotsAdmin = () =>
  api.get('/admin/export/bulk-lots-admin', { responseType: 'blob' });

// Admin Alert Settings (cache-busted to avoid stale proxy/CDN responses)
export const getWaStatus = () => api.get('/admin/alerts/wa-diagnostic');
export const registerWaPhone = (pin) => api.post('/admin/alerts/wa-register', { pin });

// ── Master Customers (Phase 2) ────────────────────────────────────────
export const listCustomers = (params = {}) => api.get('/admin/customers', { params });
export const getCustomer = (id) => api.get(`/admin/customers/${id}`);
export const createCustomer = (data) => api.post('/admin/customers', data);
export const patchCustomer = (id, data) => api.patch(`/admin/customers/${id}`, data);
export const getCustomerStats = () => api.get('/admin/customers/stats');
export const getCustomerEnums = () => api.get('/admin/customers/_meta/enums');
export const previewCustomerBackfill = () => api.get('/admin/customers/backfill/preview');
export const runCustomerBackfill = (data) => api.post('/admin/customers/backfill', data);
export const quickConvertToCustomer = (sourceKind, sourceId) =>
  api.post('/admin/customers/from-source', { source_kind: sourceKind, source_id: sourceId });
export const exportCustomersXlsxUrl = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') usp.append(k, v);
  });
  const qs = usp.toString();
  return `${API_URL}/admin/customers/export.xlsx${qs ? `?${qs}` : ''}`;
};


// ── Campaign Recipient Preview (Phase 3) ─────────────────────────────
export const getRecipientPreview = (cid, audienceType, customerFilters, supplierFilters, prospectFilters, recipientLimit, skipAlreadyContacted) =>
  api.post(`/admin/campaigns/${cid}/recipient-preview`, {
    audience_type: audienceType,
    customer_filters: customerFilters || null,
    supplier_filters: supplierFilters || null,
    prospect_filters: prospectFilters || null,
    recipient_limit: recipientLimit || null,
    skip_already_contacted: skipAlreadyContacted !== undefined ? skipAlreadyContacted : true,
  });
export const confirmRecipientPreview = (cid, audienceType, filtersHash) =>
  api.post(`/admin/campaigns/${cid}/preview-confirm`, {
    audience_type: audienceType,
    filters_hash: filtersHash,
  });

// Prospect filter dropdown options (countries/sources/batches/statuses/tiers)
export const getProspectCampaignFilterOptions = () =>
  api.get('/admin/campaigns/prospects/filter-options');


// ── Admin Settings (Phase 4 — Hide Legacy CRM toggle) ────────────────
export const getSettings = () => api.get('/admin/settings');
export const patchSettings = (data) => api.patch('/admin/settings', data);


// Quote custom-sourced items (Feb-2026 Phase C)
export const addCustomItem = (quoteId, payload) =>
  api.post(`/admin/quotes/${quoteId}/items/custom`, payload);
export const patchQuoteItem = (quoteId, itemId, payload) =>
  api.patch(`/admin/quotes/${quoteId}/items/${itemId}`, payload);
export const deleteQuoteItem = (quoteId, itemId) =>
  api.delete(`/admin/quotes/${quoteId}/items/${itemId}`);
export const seedCustomFromSourcing = (quoteId) =>
  api.post(`/admin/quotes/${quoteId}/seed-custom-from-sourcing`);
